import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import React, { useEffect } from 'react';
import clsx from 'clsx';
import { Direction } from '../common/enums';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { ReactComponent as IcoArrow } from '../assets/svg/prev.svg';
import { fetchData, clearData } from '../common/app/actions';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { useDynamicTranslations } from '../common/hooks/useTranslations';

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      prevNextContainerStyle: {
        position: 'relative',
        width: '100%',
        // height: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      },
      iconWrapperStyle: {
        width: '40px',
        height: '40px',
        minWidth: '40px',
        minHeight: '40px',
        borderRadius: '50%',
        border: `1px solid ${COLORS.textGray}`,
        background: COLORS.textGray,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      prevStyle: {
        marginRight: '10px',
      },
      nextStyle: {
        marginLeft: '10px',
      },
      iconStyle: {
        width: '20px',
        fill: COLORS.white,
        paddingRight: '2px',
      },
      iconNextStyle: {
        transform: 'rotate(180deg)',
      },
      [`@media (max-width: 840px)`]: {
        prevNextContainerStyle: {
          justifyContent: 'flex-start',
        },
      },
    })
);

const PrevNextAnswer = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const {
    themeId: answerThemeId,
    surveyId: answerSurveyId,
    answerId,
  } = useParams();

  const prevTooltip = useDynamicTranslations('prev_tooltip');
  const nextTooltip = useDynamicTranslations('next_tooltip');

  const answersPreviewData = useSelector(
    (state: any) => state.app.answersPreviewData
  );

  useEffect(() => {
    dispatch(clearData(C.ANSWERS_PREVIEW_DATA));

    dispatch(
      fetchData(C.ANSWERS_PREVIEW_DATA, {
        answersThemeId: answerThemeId,
        dynamicimages: true,
      })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // console.log('>>>>222', answerThemeId, answerSurveyId, answerId);

  if (!answersPreviewData || !answersPreviewData?.pages) {
    return null;
  }

  const currentSurveys = answersPreviewData.pages?.find(
    (item) => item.content.uid === answerSurveyId
  );

  const totalSurveys = currentSurveys?.pages?.length;

  if (!currentSurveys || !currentSurveys?.pages || totalSurveys === 1) {
    return null;
  }

  const getNewIndex = (direction: Direction, currentIndex: number) => {
    if (direction === Direction.LEFT) {
      return currentIndex === 0 ? totalSurveys - 1 : currentIndex - 1;
    }

    return currentIndex === totalSurveys - 1 ? 0 : currentIndex + 1;
  };

  const onClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const direction: Direction = event.currentTarget.getAttribute(
      'data-direction'
    ) as Direction;

    const currentIndex = currentSurveys?.pages?.findIndex(
      (item) => item.content.uid === answerId
    );

    const newIndex = getNewIndex(direction, currentIndex);
    const newAnswerData = currentSurveys?.pages?.[newIndex];
    const newAnswerId = newAnswerData?.content?.uid;

    navigate({
      pathname: `${ROUTES.THEME}/${answerThemeId}/${answerSurveyId}/${newAnswerId}`,
      search,
    });
  };

  return (
    <div className={classes.prevNextContainerStyle}>
      <Tooltip title={prevTooltip} placement={'top-start'} arrow>
        <div
          className={clsx(classes.iconWrapperStyle, classes.prevStyle)}
          onClick={onClick}
          data-direction={Direction.LEFT}
        >
          <IcoArrow className={classes.iconStyle} />
        </div>
      </Tooltip>
      <Tooltip title={nextTooltip} placement={'top-end'} arrow>
        <div
          className={clsx(classes.iconWrapperStyle, classes.nextStyle)}
          onClick={onClick}
          data-direction={Direction.RIGHT}
        >
          <IcoArrow
            className={clsx(classes.iconStyle, classes.iconNextStyle)}
          />
        </div>
      </Tooltip>
    </div>
  );
};

export default PrevNextAnswer;
